import { z } from "zod";

import { CLAIM_TOKEN_COOKIE_MAX_AGE_SECONDS } from "@/lib/payments/payment-timeouts";

export const CLAIM_TOKEN_COOKIE = "hyred_claim_token";

const STORAGE_KEY = "hyred_guest_checkout";

/**
 * How long a persisted guest checkout stays restorable on this device. The
 * emailed claim link is the long-term recovery path; local restore only needs
 * to cover a same-day tab close or refresh.
 */
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

/** Mirrors the server-side schema in /api/payment/guest-bundle. */
export const guestEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(320);

const storedCheckoutSchema = z.object({
  paymentId: z.string().min(1),
  claimToken: z.string().min(1),
  amount: z.number(),
  qrString: z.string().nullable(),
  invoiceUrl: z.string().url().nullable().default(null),
  savedAt: z.number(),
});

export type StoredGuestCheckout = z.infer<typeof storedCheckoutSchema>;

/** Claim-token cookie shared with the OAuth redirect and /api/payment/claim. */
export function setClaimTokenCookie(claimToken: string): void {
  document.cookie = `${CLAIM_TOKEN_COOKIE}=${encodeURIComponent(claimToken)}; path=/; max-age=${CLAIM_TOKEN_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

/**
 * Persists the in-flight guest checkout the moment the QR is issued so a tab
 * close or refresh does not orphan a paid-but-unclaimed purchase. Storage
 * failures (private mode, quota) are swallowed: the cookie and the emailed
 * claim link still cover recovery.
 */
export function saveGuestCheckout(
  entry: Omit<StoredGuestCheckout, "savedAt">,
): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...entry, savedAt: Date.now() }),
    );
  } catch {
    // Best-effort persistence only.
  }
}

/** Reads a previously persisted checkout; drops malformed or expired entries. */
export function readGuestCheckout(): StoredGuestCheckout | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = storedCheckoutSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      clearGuestCheckout();
      return null;
    }

    if (Date.now() - parsed.data.savedAt > STORAGE_TTL_MS) {
      clearGuestCheckout();
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
}

export function clearGuestCheckout(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort persistence only.
  }
}
